import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Receipt, Timer, CheckSquare, FileText, FileSignature, Users, Wallet, Plus, AlertTriangle,
  Phone, Mail, ExternalLink, NotebookPen,
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import NotesPanel from '@/components/notes/NotesPanel';
import { quotesAPI, invoicesAPI, fmtMoney, fmtDay, fmtHours, QUOTE_STATUS, INVOICE_STATUS } from '@/lib/financeAPI';

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';
const card = 'rounded-xl border border-[#E3DDD1] bg-white';
const serif = { fontFamily: 'Fraunces, Georgia, serif' };
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const hubAPI = {
  summary: () => api.get('/api/projects/summary'),
  overview: (id) => api.get(`/api/projects/${id}/overview`),
};

const Pill = ({ status, map }) => {
  const s = map[status] || { label: status, cls: 'bg-[#F3EFE7] text-[#5C6670]' };
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${s.cls}`}>{s.label}</span>;
};

const SIGN = {
  pending: { label: 'Awaiting', cls: 'bg-amber-50 text-amber-800' }, partially_signed: { label: 'In progress', cls: 'bg-amber-50 text-amber-800' },
  awaiting_execution: { label: 'Your part', cls: 'bg-sky-50 text-sky-800' }, executed: { label: 'Signed', cls: 'bg-emerald-50 text-emerald-800' },
};
const TASK = { backlog: 'To do', in_progress: 'In progress', review: 'Review', done: 'Done' };

const Section = ({ icon: Icon, title, action, children, empty }) => (
  <div className={`${card} overflow-hidden`}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E3DDD1]">
      <Icon className="w-4 h-4 text-[#A62B1F]" /><h3 className="font-semibold text-[#1E2A33] flex-1">{title}</h3>{action}
    </div>
    {empty ? <div className="px-4 py-5 text-sm text-[#5C6670]">{empty}</div> : children}
  </div>
);

const Money = ({ m, mods }) => {
  const tiles = [];
  if (mods.includes('quotes')) tiles.push(['Quoted (accepted)', fmtMoney(m.quoted), m.quotes_open ? `${fmtMoney(m.quotes_open)} waiting on the client` : null]);
  if (mods.includes('invoices')) {
    tiles.push(['Invoiced', fmtMoney(m.invoiced), `${fmtMoney(m.paid)} paid`]);
    tiles.push(['Owed', fmtMoney(m.owed), m.overdue ? <span className="text-[#A62B1F] font-semibold">{fmtMoney(m.overdue)} overdue</span> : 'Nothing overdue']);
  }
  if (mods.includes('time') || mods.includes('expenses')) {
    tiles.push(['Done, not yet billed', fmtMoney((m.unbilled_time || 0) + (m.unbilled_expenses || 0)),
      [m.unbilled_hours ? fmtHours(m.unbilled_hours) : null, m.unbilled_expenses ? `${fmtMoney(m.unbilled_expenses)} expenses` : null].filter(Boolean).join(' + ') || 'All billed']);
  }
  if (mods.includes('time')) tiles.push(['Hours logged', fmtHours(m.hours), m.spent ? `${fmtMoney(m.spent)} spent` : null]);
  if (!tiles.length) return null;
  return (
    <div className={`grid grid-cols-2 ${tiles.length > 4 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3`}>
      {tiles.map(([k, v, n]) => (
        <div key={k} className={`${card} p-4`}>
          <div className="text-[11px] uppercase tracking-wider text-[#5C6670] font-semibold">{k}</div>
          <div className="text-xl font-semibold mt-1 text-[#1E2A33]">{v}</div>
          {n && <div className="text-xs text-[#5C6670] mt-0.5">{n}</div>}
        </div>
      ))}
    </div>
  );
};

// ── All projects ─────────────────────────────────────────────────────────────
const ProjectList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { hubAPI.summary().then((r) => setData(r.data)).catch((e) => err(e, 'Could not load projects')); }, []);
  const mods = data?.modules || [];
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><h1 className="text-2xl font-semibold text-[#1E2A33] mr-auto" style={serif}>Projects</h1>
        <Button variant="outline" onClick={() => navigate('/settings/projects')}>Manage projects</Button></div>
      {!data ? <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div> : data.projects.length === 0 ? (
        <div className={`${card} p-8 text-center text-sm text-[#5C6670]`}>No projects yet. Add one under Manage projects.</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.projects.map((p) => (
            <button key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className={`${card} p-4 text-left hover:border-[#1E2A33] transition-colors`}>
              <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || '#A62B1F' }} /><span className="font-semibold text-[#1E2A33] truncate">{p.name}</span></div>
              {p.description && <div className="text-xs text-[#5C6670] mt-1 line-clamp-2">{p.description}</div>}
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                {mods.includes('invoices') && <div><div className="text-[#94A3B8]">Owed</div><div className={`font-semibold ${p.money.overdue ? 'text-[#A62B1F]' : 'text-[#1E2A33]'}`}>{fmtMoney(p.money.owed)}</div></div>}
                {(mods.includes('time') || mods.includes('expenses')) && <div><div className="text-[#94A3B8]">To bill</div><div className="font-semibold text-[#1E2A33]">{fmtMoney((p.money.unbilled_time || 0) + (p.money.unbilled_expenses || 0))}</div></div>}
                <div><div className="text-[#94A3B8]">Open tasks</div><div className="font-semibold text-[#1E2A33]">{p.open_tasks}</div></div>
              </div>
              <div className="text-[11px] text-[#94A3B8] mt-2">{p.people} {p.people === 1 ? 'person' : 'people'}{mods.includes('time') && p.money.hours ? ` · ${fmtHours(p.money.hours)} logged` : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── One project ──────────────────────────────────────────────────────────────
const ProjectPage = ({ id }) => {
  const navigate = useNavigate();
  const { projects = [], switchProject } = useProject() || {};
  const [o, setO] = useState(null);
  useEffect(() => { setO(null); hubAPI.overview(id).then((r) => setO(r.data)).catch(() => { toast.error('Project not found'); navigate('/projects'); }); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!o) return <div className={`${card} p-6 text-sm text-[#5C6670]`}>Loading…</div>;
  const mods = o.modules || [];
  const p = o.project;
  const client = o.people.find((x) => x.company) || o.people[0];
  const clientFields = client ? { name: client.name, company: client.company || '', email: client.email || '', contact_id: client.id } : undefined;
  // Opens Tasks on this project (list view remembers it; the board follows the active project)
  const openBoard = () => {
    const proj = projects.find((x) => x._id === p.id); if (proj && switchProject) switchProject(proj);
    try { localStorage.setItem('stagehead.tasks.sel', JSON.stringify({ kind: 'project', id: p.id })); } catch { /* private mode */ }
    navigate('/tasks');
  };
  const newQuote = async () => { try { const r = await quotesAPI.create({ project_id: p.id, client: clientFields }); navigate(`/quotes/${r.data.id}`); } catch (e) { err(e, 'Could not start a quote'); } };
  const newInvoice = async () => { try { const r = await invoicesAPI.create({ project_id: p.id, client: clientFields }); navigate(`/invoices/${r.data.id}`); } catch (e) { err(e, 'Could not start an invoice'); } };

  return (
    <div className="space-y-4">
      <div className={`${card} p-4 flex items-start gap-3 flex-wrap`}>
        <button className="p-1 -ml-1 mt-1" onClick={() => navigate('/projects')}><ArrowLeft className="w-4 h-4" /></button>
        <span className="w-4 h-4 rounded-full mt-2" style={{ backgroundColor: p.color || '#A62B1F' }} />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-[#1E2A33]" style={serif}>{p.name}</h1>
          {p.description && <p className="text-sm text-[#5C6670]">{p.description}</p>}
          {client && <p className="text-xs text-[#5C6670] mt-0.5">Client: {client.company || client.name}{client.company && client.name !== client.company ? ` (${client.name})` : ''}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {mods.includes('quotes') && <Button variant="outline" size="sm" onClick={newQuote}><ClipboardList className="w-4 h-4 mr-1" />New quote</Button>}
          {mods.includes('invoices') && <Button variant="outline" size="sm" onClick={newInvoice}><Receipt className="w-4 h-4 mr-1" />New invoice</Button>}
          {mods.includes('time') && <Button variant="outline" size="sm" onClick={() => navigate('/time', { state: { project_id: p.id } })}><Timer className="w-4 h-4 mr-1" />Log time</Button>}
          <Button size="sm" className={red} onClick={openBoard}><CheckSquare className="w-4 h-4 mr-1" />Tasks</Button>
        </div>
      </div>

      <Money m={o.money} mods={mods} />

      <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          {(mods.includes('quotes') || mods.includes('invoices')) && (
            <Section icon={Wallet} title="Quotes and invoices" empty={!o.quotes.length && !o.invoices.length ? 'Nothing yet. Start with a quote, or invoice straight away.' : null}>
              <table className="w-full text-sm"><tbody>
                {o.quotes.map((q) => (
                  <tr key={q.id} className="border-t border-[#F0EBE1] first:border-0 hover:bg-[#FBF8F2] cursor-pointer" onClick={() => navigate(`/quotes/${q.id}`)}>
                    <td className="p-2.5 pl-4 w-28"><span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{q.kind === 'change_order' ? 'Change' : 'Quote'}</span><div className="font-semibold text-[#1E2A33] whitespace-nowrap">{q.label}</div></td>
                    <td className="p-2.5 text-[#5C6670] truncate max-w-[240px]">{q.title}</td>
                    <td className="p-2.5 text-right whitespace-nowrap">{fmtMoney(q.total, q.currency)}</td>
                    <td className="p-2.5 pr-4 text-right"><Pill status={q.status} map={QUOTE_STATUS} /></td>
                  </tr>
                ))}
                {o.invoices.map((i) => (
                  <tr key={i.id} className="border-t border-[#F0EBE1] hover:bg-[#FBF8F2] cursor-pointer" onClick={() => navigate(`/invoices/${i.id}`)}>
                    <td className="p-2.5 pl-4"><span className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Invoice</span><div className="font-semibold text-[#1E2A33] whitespace-nowrap">{i.number}</div></td>
                    <td className="p-2.5 text-[#5C6670]">{i.quote_number ? `From ${i.quote_number}${i.quote_part ? ` (${String(i.quote_part).split(':')[0]})` : ''}` : fmtDay(i.issue_date)}{i.balance > 0 && i.status !== 'draft' && i.status !== 'void' ? ` · due ${fmtDay(i.due_date, { month: 'short', day: 'numeric' })}` : ''}</td>
                    <td className="p-2.5 text-right whitespace-nowrap">{fmtMoney(i.total, i.currency)}{i.balance > 0 && i.balance !== i.total && <div className="text-[11px] text-[#5C6670]">{fmtMoney(i.balance)} left</div>}</td>
                    <td className="p-2.5 pr-4 text-right"><Pill status={i.status} map={INVOICE_STATUS} /></td>
                  </tr>
                ))}
              </tbody></table>
            </Section>
          )}

          <Section icon={CheckSquare} title="Tasks" action={<button className="text-xs font-semibold text-[#A62B1F]" onClick={openBoard}>All tasks</button>}
            empty={!o.tasks.open.length && !o.tasks.counts.done ? 'No tasks on this project yet.' : null}>
            <div className="px-4 py-2 flex gap-3 text-xs text-[#5C6670] border-b border-[#F0EBE1] flex-wrap">
              {Object.entries(o.tasks.counts).map(([k, v]) => <span key={k}>{TASK[k] || k}: <strong className="text-[#1E2A33]">{v}</strong></span>)}
              {o.tasks.overdue > 0 && <span className="text-[#A62B1F] font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{o.tasks.overdue} overdue</span>}
            </div>
            {o.tasks.open.map((t) => (
              <div key={t.id} className="flex items-center gap-3 px-4 py-2 border-t border-[#F0EBE1] first:border-0 text-sm">
                <span className={`w-2 h-2 rounded-full shrink-0 ${t.priority === 'urgent' ? 'bg-[#A62B1F]' : t.priority === 'high' ? 'bg-amber-500' : 'bg-[#C9C0B0]'}`} title={t.priority} />
                <span className="flex-1 truncate text-[#1E2A33]">{t.title}</span>
                <span className="text-xs text-[#5C6670] hidden sm:inline">{t.assignee || ''}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3EFE7] text-[#5C6670]">{TASK[t.status]}</span>
                {t.due_date && <span className={`text-xs w-14 text-right ${t.overdue ? 'text-[#A62B1F] font-semibold' : 'text-[#5C6670]'}`}>{fmtDay(String(t.due_date).slice(0, 10), { month: 'short', day: 'numeric' })}</span>}
              </div>
            ))}
          </Section>

          <Section icon={NotebookPen} title="Notes">
            <div className="p-4"><NotesPanel projectId={p.id} header={false} emptyText="Meeting notes, decisions, ideas. Lines starting with [ ] can become tasks on this project." /></div>
          </Section>

          <Section icon={FileText} title="Documents and signatures" action={<Link to="/documents" className="text-xs font-semibold text-[#A62B1F]">Documents</Link>}
            empty={!o.documents.length && !o.signatures.length ? 'No documents tagged to this project yet. Tag them when you upload in Documents.' : null}>
            {o.signatures.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-2 border-t border-[#F0EBE1] first:border-0 text-sm">
                <FileSignature className="w-4 h-4 text-[#94A3B8] shrink-0" /><span className="flex-1 truncate">{s.title}</span>
                <span className="text-xs text-[#5C6670]">{s.signed} of {s.of} signed</span><Pill status={s.status} map={SIGN} />
              </div>
            ))}
            {o.documents.filter((d) => !o.signatures.some((s) => s.title === d.title)).map((d) => (
              <div key={d.id} className="flex items-center gap-3 px-4 py-2 border-t border-[#F0EBE1] first:border-0 text-sm">
                <FileText className="w-4 h-4 text-[#94A3B8] shrink-0" /><span className="flex-1 truncate">{d.title}</span>
                <span className="text-xs text-[#94A3B8]">{d.created_at ? fmtDay(String(d.created_at).slice(0, 10), { month: 'short', day: 'numeric' }) : ''}</span>
              </div>
            ))}
          </Section>
        </div>

        <div className="space-y-4">
          <Section icon={Users} title="People" action={<Link to="/contacts" className="text-xs font-semibold text-[#A62B1F]">Contacts</Link>} empty={!o.people.length ? 'Add contacts to this project from Contacts.' : null}>
            {o.people.slice(0, 12).map((c) => (
              <div key={c.id} className="px-4 py-2 border-t border-[#F0EBE1] first:border-0">
                <div className="flex items-center gap-2"><span className="text-sm font-medium text-[#1E2A33] flex-1 truncate">{c.name}</span>{c.stage_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F3EFE7] text-[#5C6670]">{c.stage_name}</span>}</div>
                <div className="flex items-center gap-3 text-xs text-[#5C6670]">{c.company && c.company !== c.name && <span className="truncate">{c.company}</span>}
                  {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#A62B1F]"><Mail className="w-3 h-3" />Email</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[#A62B1F]"><Phone className="w-3 h-3" />Call</a>}</div>
              </div>
            ))}
            {o.people.length > 12 && <div className="px-4 py-2 text-xs text-[#5C6670] border-t border-[#F0EBE1]">and {o.people.length - 12} more</div>}
          </Section>

          {o.time && (
            <Section icon={Timer} title="Time" action={<Link to="/time" className="text-xs font-semibold text-[#A62B1F]">Timesheet</Link>} empty={!o.time.hours ? 'No time logged yet. Use the timer at the top of any page.' : null}>
              <div className="px-4 py-2 text-sm flex gap-4 border-b border-[#F0EBE1]"><span><strong>{fmtHours(o.time.hours)}</strong> total</span><span className="text-[#5C6670]">{fmtHours(o.time.this_month)} this month</span></div>
              {o.time.by_person.length > 1 && <div className="px-4 py-1.5 text-xs text-[#5C6670] border-b border-[#F0EBE1]">{o.time.by_person.map((x) => `${x.name} ${fmtHours(x.hours)}`).join(' · ')}</div>}
              {o.time.recent.map((r, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-1.5 text-xs border-t border-[#F0EBE1] first:border-0">
                  <span className="w-12 text-[#5C6670]">{fmtDay(r.date, { month: 'short', day: 'numeric' })}</span>
                  <span className="flex-1 truncate">{[r.activity, r.description].filter(Boolean).join(' · ') || '—'}</span>
                  <span className="tabular-nums">{fmtHours(r.hours)}</span>{r.billed && <span className="text-[10px] text-sky-700">billed</span>}
                </div>
              ))}
            </Section>
          )}

          {o.expenses && o.expenses.count > 0 && (
            <Section icon={Wallet} title="Expenses" action={<Link to="/expenses" className="text-xs font-semibold text-[#A62B1F]">All</Link>}>
              {o.expenses.recent.map((e, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-1.5 text-xs border-t border-[#F0EBE1] first:border-0">
                  <span className="w-12 text-[#5C6670]">{fmtDay(e.date, { month: 'short', day: 'numeric' })}</span><span className="flex-1 truncate">{e.vendor}</span>
                  <span>{fmtMoney(e.amount)}</span>{e.billable && <span className={`text-[10px] ${e.billed ? 'text-sky-700' : 'text-amber-700'}`}>{e.billed ? 'billed' : 'to bill'}</span>}
                </div>
              ))}
            </Section>
          )}

          <Section icon={Plus} title="What's happened" empty={!o.timeline.length ? 'Activity on this project will show here.' : null}>
            {o.timeline.map((t, i) => (
              <button key={i} onClick={() => t.link && navigate(t.link)} className="w-full text-left flex gap-2 px-4 py-1.5 text-xs border-t border-[#F0EBE1] first:border-0 hover:bg-[#FBF8F2]">
                <span className="w-12 shrink-0 text-[#94A3B8]">{new Date(t.at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>
                <span className="flex-1 text-[#1E2A33]">{t.text}</span>{t.link && <ExternalLink className="w-3 h-3 text-[#C9C0B0] shrink-0 mt-0.5" />}
              </button>
            ))}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default function ProjectHub() {
  const { id } = useParams();
  return <Layout title="Projects">{id ? <ProjectPage key={id} id={id} /> : <ProjectList />}</Layout>;
}
